import React from 'react';
import { Check } from 'lucide-react';

// Custom icons matching the design mockup - MINIMALIST VERSION with thinner lines

// Step 1: Document with horizontal lines (Project Details)
const DocumentIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="12" height="16" rx="1" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="9" y1="12" x2="15" y2="12" />
    <line x1="9" y1="16" x2="15" y2="16" />
  </svg>
);

// Step 2: Layered rectangles with dots (Design Preferences)
const LayersIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="7" width="14" height="10" rx="1" />
    <rect x="7" y="5" width="10" height="2" rx="0.5" />
    <circle cx="10" cy="11" r="0.6" fill="currentColor" />
    <circle cx="12" cy="11" r="0.6" fill="currentColor" />
    <circle cx="14" cy="11" r="0.6" fill="currentColor" />
    <circle cx="10" cy="13" r="0.6" fill="currentColor" />
    <circle cx="12" cy="13" r="0.6" fill="currentColor" />
    <circle cx="14" cy="13" r="0.6" fill="currentColor" />
  </svg>
);

// Step 3: Radiating lines/burst (AI Analysis)
const AnalysisIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
  </svg>
);

// Step 4: Two people silhouettes (Contact Info)
const PeopleIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="7" r="2" />
    <path d="M5.5 13v0a3.5 3.5 0 0 1 7 0v4h-7v-4Z" />
    <circle cx="15" cy="7" r="2" />
    <path d="M11.5 13v0a3.5 3.5 0 0 1 7 0v4h-7v-4Z" />
  </svg>
);

/**
 * Multi-step form progress indicator with animations
 */
export const ProgressIndicator = ({ 
  currentStep, 
  totalSteps = 4, 
  className = "",
  variant = "horizontal" 
}) => {
  const steps = [
    { 
      id: 1, 
      name: "Project Details", 
      icon: DocumentIcon,
      description: "Surface area and requirements"
    },
    { 
      id: 2, 
      name: "Design Preferences", 
      icon: LayersIcon,
      description: "Concept, finish & furniture"
    },
    { 
      id: 3, 
      name: "AI Analysis", 
      icon: AnalysisIcon,
      description: "AI workplace expert & floor plan"
    },
    { 
      id: 4, 
      name: "Contact Information", 
      icon: PeopleIcon,
      description: "Get your personalized estimate"
    }
  ];

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepClasses = (status) => {
    const baseClasses = "transition-all duration-300";
    
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-brand-gold border-brand-gold text-white`;
      case 'current':
        return `${baseClasses} border-brand-gold text-brand-gold bg-brand-light-grey ring-2 ring-brand-gold ring-opacity-50`;
      case 'upcoming':
        return `${baseClasses} border-gray-300 text-gray-400 bg-brand-light-grey`;
      default:
        return baseClasses;
    }
  };

  if (variant === "vertical") {
    return (
      <div className={`space-y-4 ${className}`}>
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const status = getStepStatus(stepNumber);
          const Icon = step.icon;
          
          return (
            <div key={step.id} className="flex items-start space-x-4">
              <div className="flex flex-col items-center">
                <div 
                  className={`
                    w-10 h-10 rounded-full border-2 flex items-center justify-center
                    ${getStepClasses(status)}
                  `}
                >
                  {status === 'completed' ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div 
                    className={`
                      w-px h-8 mt-2 transition-colors duration-300
                      ${status === 'completed' ? 'bg-brand-gold' : 'bg-gray-300'}
                    `}
                  />
                )}
              </div>
              
              <div className="flex-1 pb-8">
                <h3 className={`
                  text-sm font-medium transition-colors duration-300
                  ${status === 'completed' || status === 'current' ? 'text-brand-black' : 'text-gray-400'}
                `}>
                  {step.name}
                </h3>
                <p className={`
                  text-xs mt-1 transition-colors duration-300
                  ${status === 'completed' || status === 'current' ? 'text-brand-grey' : 'text-gray-400'}
                `}>
                  {step.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Horizontal variant (default)
  return (
    <div className={`w-full ${className}`}>
      {/* Progress bar with proper spacing */}
      <div className="mb-16">
        <div className="flex items-start justify-between">
          {steps.map((step, index) => {
            const stepNumber = index + 1;
            const status = getStepStatus(stepNumber);
            const Icon = step.icon;
            const isLast = index === steps.length - 1;
            
            return (
              <div key={step.id} className="flex flex-col items-center relative" style={{width: `${100/steps.length}%`}}>
                {/* Step circle */}
                <div 
                  className={`
                    w-12 h-12 rounded-full border-2 flex items-center justify-center mb-4
                    ${getStepClasses(status)}
                    ${status === 'current' ? 'animate-pulse' : ''}
                  `}
                >
                  {status === 'completed' ? (
                    <Check className="w-6 h-6 animate-bounce-gentle" />
                  ) : (
                    <Icon className={`w-6 h-6 ${status === 'current' ? 'animate-bounce-gentle' : ''}`} />
                  )}
                </div>
                
                {/* Connecting line - positioned behind circles */}
                {!isLast && (
                  <div className="absolute top-6 left-1/2 w-full h-px -z-10">
                    <div 
                      className={`
                        h-full transition-all duration-500 ease-out ml-6
                        ${status === 'completed' ? 'bg-brand-gold' : 'bg-gray-300'}
                      `}
                      style={{
                        background: status === 'completed' 
                          ? 'linear-gradient(90deg, hsl(var(--brand-gold)), hsl(var(--brand-gold)))'
                          : status === 'current'
                          ? 'linear-gradient(90deg, hsl(var(--brand-gold)) 50%, #d1d5db 50%)'
                          : '#d1d5db'
                      }}
                    />
                  </div>
                )}
                
                {/* Step label - properly spaced below circle */}
                <div className="text-center px-1 mt-1">
                  <h4 className={`
                    text-xs font-medium transition-colors duration-300 mb-1 leading-normal
                    ${status === 'completed' || status === 'current' ? 'text-brand-black' : 'text-gray-400'}
                  `}>
                    {step.name}
                  </h4>
                  <p className={`
                    text-xs transition-colors duration-300 leading-tight hidden md:block
                    ${status === 'completed' || status === 'current' ? 'text-brand-grey' : 'text-gray-400'}
                  `}>
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Progress summary */}
      <div className="flex items-center justify-center text-sm">
        <span className="text-brand-grey">
          Step {currentStep} of {totalSteps}
        </span>
      </div>
    </div>
  );
};

/**
 * Compact progress bar for mobile
 */
export const CompactProgress = ({ currentStep, totalSteps = 4, className = "" }) => {
  const progress = (currentStep / totalSteps) * 100;
  
  const steps = [
    "Project Details", "Design Preferences", "Enhanced Features", "Contact Information"
  ];
  
  return (
    <div className={`w-full ${className}`}>
      <div className="mb-3">
        <span className="text-sm font-medium text-brand-black">
          Step {currentStep} of {totalSteps}
        </span>
        <p className="text-xs text-brand-grey mt-1">
          {steps[currentStep - 1]}
        </p>
      </div>
      
      <div className="w-full bg-brand-light-grey rounded-full h-4 overflow-hidden mb-2">
        <div 
          className="h-full bg-gradient-to-r from-brand-gold to-yellow-400 transition-all duration-500 ease-out rounded-full"
          style={{ width: `${progress}%` }}
        />
      </div>
      
      <p className="text-xs text-center text-brand-grey">
        {progress < 100 ? 'Complete the current step to continue' : 'Ready to submit!'}
      </p>
    </div>
  );
};

/**
 * Animated step indicator for current step
 */
export const CurrentStepIndicator = ({ step, title, description, className = "" }) => {
  const steps = [
    { icon: DocumentIcon, color: "text-brand-gold" },
    { icon: LayersIcon, color: "text-brand-gold" },
    { icon: AnalysisIcon, color: "text-brand-gold" },
    { icon: PeopleIcon, color: "text-brand-gold" }
  ];
  
  const currentStepConfig = steps[step - 1] || steps[0];
  const Icon = currentStepConfig.icon;
  
  return (
    <div className={`flex items-center space-x-4 p-4 bg-white border border-brand-gold rounded-lg animate-fade-in ${className}`}>
      <div className="relative">
        <div className="absolute inset-0 animate-ping">
          <Icon className={`w-8 h-8 ${currentStepConfig.color} opacity-20`} />
        </div>
        <Icon className={`w-8 h-8 ${currentStepConfig.color}`} />
      </div>
      
      <div>
        <h3 className="font-semibold text-brand-black">{title}</h3>
        <p className="text-sm text-brand-grey">{description}</p>
      </div>
    </div>
  );
};

export default ProgressIndicator;